/* eslint-disable no-unused-vars */
import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Box,
  IconButton,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  OutlinedInput,
} from '@mui/material';
import useTenantMutation from '@/hooks/useTenantMutation';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { ButtonGroup } from '@mui/material';
import { toast } from 'react-toastify';
import InterfaceTypeFields from '@components/forms/interfaces/InterfaceTypeFields';
import useTenantQuery from '@/hooks/useTenantQuery';
import axios from 'axios';
import tenant from '@/services/TenantService';

const INTERFACE_TYPES = [
  { value: 'nexmart', label: 'Nexmart' },
  { value: 'nexmart_media', label: 'Nexmart Media' },
  { value: 'weclapp', label: 'Weclapp' },
  { value: 'tanss', label: 'TANSS' },
  { value: 'tanss-erp', label: 'TANSS-ERP' },
  { value: 'vmware', label: 'VM-Ware' },
  { value: 'ftp', label: 'FTP' },
  { value: 'backup42', label: 'Acronis Backup42' },
  { value: 'host', label: 'Host' },
  { value: 'jira', label: 'Jira' },
  { value: 'acronis', label: 'Acronis' },
  { value: 'otto', label: 'Otto' },
  { value: 'paperless', label: 'Paperless' },
  { value: 'shipcloud', label: 'Shipcloud' },
  { value: 'riverbird', label: 'Riverbird' },
  { value: 'ups', label: 'UPS' },
  { value: 'microsoft', label: 'Microsoft' },
  { value: 'ai', label: 'AI' },
  { value: 'm365_email', label: 'M365 Email' },
  // { value: 'openroute', label: 'OpenRoute' }, // TODO: Implement OpenRoute fields
];

export default function TenantInterfaceDialog({
  open,
  onClose,
  defaultType = '',
  onSave,
}) {
  const [fields, setFields] = useState({
    type: defaultType,
    description: '',
    host: '',
    status: 'active',
    whitelist: {},
    blacklist: {},
  });
  const [typeFields, setTypeFields] = useState({});
  const [hostError, setHostError] = useState('');

  const [pathInput, setPathInput] = useState('');
  const [selectedMethods, setSelectedMethods] = useState({
    get: false,
    post: false,
    put: false,
    delete: false,
  });

  const [listType, setListType] = useState('whitelist');

  const [showTokens, setShowTokens] = useState({
    apiToken: false,
    refreshToken: false,
    authToken: false,
    AuthenticationToken: false,
    Authorization: false,
    ['X-NM-API-KEY']: false,
    tanssPassword: false,
  });

  const { mutateAsync: saveInterface } = useTenantMutation('interface');

  const { data: roles } = useTenantQuery('role');
  const [selectedRoles, setselectedRoles] = useState([]);

  const handleUserChange = (event) => {
    const shareObject = event.target.value.map((roleId) => ({
      type: 'role',
      id: roleId,
    }));
    setselectedRoles(event.target.value);

    setFields((prev) => ({
      ...prev,
      sharedWith: shareObject,
    }));
  };

  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: '20%',
      },
    },
  };

  const handleChange = (field) => (event) => {
    if (field === 'type') {
      const newType = event.target.value;
      setFields((prev) => ({
        ...prev,
        type: newType,
      }));
      // Reset type-specific fields when type changes
      setTypeFields({});
    } else if (field === 'host') {
      let newHost = event.target.value;

      if (newHost.startsWith('http://') || newHost.startsWith('https://')) {
        newHost = newHost.replace(/^https?:\/\//, '');
      }

      if (validateHost(newHost)) {
        setHostError('');
      } else {
        setHostError(
          'Bitte geben Sie einen gültigen Hostnamen oder IP-Adresse ein',
        );
      }
      setFields((prev) => ({
        ...prev,
        [field]: newHost,
      }));
    } else {
      setFields((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));
    }
  };

  const validateHost = (host) => {
    // Allow empty value while typing
    if (!host) return true;

    // Match hostname (e.g., example.com) or IP address
    const hostnameRegex =
      /^[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    const ipRegex = /^(\d{1,3}\.){3}\d{1,3}$/;

    if (hostnameRegex.test(host) || ipRegex.test(host)) {
      return true;
    }
    return false;
  };

  const handleRemovePath = (pathToRemove) => {
    setFields((prev) => {
      const newList = { ...prev[listType] };
      delete newList[pathToRemove];
      return {
        ...prev,
        [listType]: newList,
      };
    });
  };

  const handleAddPath = () => {
    if (!pathInput.trim()) return;

    const selectedMethodCount =
      Object.values(selectedMethods).filter(Boolean).length;

    setFields((prev) => ({
      ...prev,
      [listType]: {
        ...prev[listType],
        [pathInput]:
          selectedMethodCount === 0
            ? true
            : Object.entries(selectedMethods)
                .filter(([_, isSelected]) => isSelected)
                .reduce((acc, [method]) => {
                  acc[method] = true;
                  return acc;
                }, {}),
      },
    }));

    // Reset inputs
    setPathInput('');
    setSelectedMethods({
      get: false,
      post: false,
      put: false,
      delete: false,
    });
  };

  const handleSubmit = async () => {
    try {
      // Special handling for M365 OAuth
      if (fields.type === 'm365_email') {
        // Store description and mailbox folder in sessionStorage for callback
        sessionStorage.setItem('m365_interface_description', fields.description);
        sessionStorage.setItem('m365_mailbox_folder', typeFields.mailboxFolder || 'Inbox');
        
        // Get OAuth URL from backend
        const response = await tenant.post(`${import.meta.env.VITE_TENANT_URL}/interface/m365/authorize`);
        
        // Redirect to Microsoft OAuth
        window.location.href = response.data.authUrl;
        return;
      }

      // Normal interface creation for other types
      const submitData = {
        ...fields,
        ...typeFields,
      };

      await saveInterface(submitData);
      if (onSave) {
        onSave(submitData);
      }
      onClose();
    } catch (error) {
      toast.error('Failed to save interface');
    }
  };

  // Currently not used, can be re-added later if needed
  const endpointControl = (
    <FormControl fullWidth>
      <InputLabel shrink>API Endpunkte</InputLabel>
      <Box sx={{ mt: 3 }}>
        <ButtonGroup variant="contained" sx={{ mb: 2, width: '100%' }}>
          <Button
            fullWidth
            onClick={() => setListType('whitelist')}
            color={listType === 'whitelist' ? 'primary' : 'inherit'}
          >
            Whitelist
          </Button>
          <Button
            fullWidth
            onClick={() => setListType('blacklist')}
            color={listType === 'blacklist' ? 'primary' : 'inherit'}
          >
            Blacklist
          </Button>
        </ButtonGroup>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ width: '100%' }}
        >
          <TextField
            fullWidth
            label="API Pfad"
            value={pathInput}
            onChange={(e) => setPathInput(e.target.value)}
            placeholder="z.B. /api/v1/users"
            sx={{
              flexBasis: '30%',
              flexGrow: 0,
              minWidth: 0,
            }}
          />
          <ToggleButtonGroup
            value={Object.entries(selectedMethods)
              .filter(([_, value]) => value)
              .map(([method]) => method.toUpperCase())}
            onChange={(event, newMethods) => {
              const updated = {
                get: false,
                post: false,
                put: false,
                delete: false,
              };
              newMethods.forEach((method) => {
                updated[method.toLowerCase()] = true;
              });
              setSelectedMethods(updated);
            }}
            aria-label="HTTP Methods"
            size="small"
            sx={{
              flexBasis: '55%',
              width: '55%',
              flexGrow: 0,
              minWidth: 0,
              height: '56px',
              gap: 1,
              '& .MuiToggleButton-root': {
                borderRadius: 2,
                fontWeight: 600,
                px: 2,
                width: 60,
                minWidth: 60,
                color: 'text.primary',
                backgroundColor: 'grey.100',
                border: '1px solid transparent',
                transition: 'none',
                margin: 0,
                '&.Mui-selected': {
                  color: '#fff',
                  backgroundColor: 'primary.main',
                  border: '1px solid transparent',
                },
                '&:hover': {
                  backgroundColor: 'primary.dark',
                  color: '#fff',
                },
              },
            }}
          >
            {['GET', 'POST', 'PUT', 'DELETE'].map((method) => (
              <ToggleButton key={method} value={method} aria-label={method}>
                {method}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <IconButton
            onClick={handleAddPath}
            color="primary"
            sx={{
              flexBasis: '15%',
              flexGrow: 0,
              minWidth: 0,
              width: '15%',
              height: '56px',
            }}
          >
            <AddIcon />
          </IconButton>
        </Stack>

        <Box sx={{ mt: 2 }}>
          {Object.entries(fields[listType] || {}).map(([path, methods]) => (
            <Box
              key={path}
              sx={{
                mb: 1,
                p: 1,
                bgcolor: 'grey.100',
                borderRadius: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box>
                <Typography variant="body2">{path}</Typography>
                <Typography variant="caption" color="textSecondary">
                  {methods === true
                    ? 'ALL METHODS'
                    : Object.keys(methods).join(', ').toUpperCase()}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => handleRemovePath(path)}
                color="error"
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
        </Box>
      </Box>
    </FormControl>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Neue Schnittstelle</DialogTitle>
      <DialogContent
        sx={{
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}
      >
        <Stack spacing={2} sx={{ mt: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Typ</InputLabel>
            <Select
              value={fields.type}
              onChange={handleChange('type')}
              label="Typ"
              disabled={!!defaultType}
            >
              {INTERFACE_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Beschreibung"
            value={fields.description}
            onChange={handleChange('description')}
            required
            placeholder="Kurze Beschreibung der Schnittstelle"
          />

          <TextField
            fullWidth
            label="Host"
            value={fields.host}
            onChange={handleChange('host')}
            error={Boolean(hostError)}
            helperText={hostError}
            placeholder="beispiel.com oder 192.168.1.1"
            sx={{ display: fields.type === 'm365_email' ? 'none' : 'block' }}
          />

          <InterfaceTypeFields
            type={fields.type}
            fields={typeFields}
            setFields={setTypeFields}
            showTokens={showTokens}
            setShowTokens={setShowTokens}
          />

          <Typography variant="body1" sx={{ mb: 3 }}>
            Wählen Sie alle Rollen aus mit denen das Interface geteilt werden
            soll.
          </Typography>

          <FormControl fullWidth variant="outlined">
            <InputLabel id="Geteilte Rollenuser-select-label">
              Geteilte Rollen
            </InputLabel>
            <Select
              labelId="Geteilte Rollenuser-select-label"
              id="user-select"
              multiple
              value={selectedRoles}
              onChange={handleUserChange}
              MenuProps={MenuProps}
              input={
                <OutlinedInput
                  id="select-multiple-chip"
                  label="Geteilte Rollen"
                />
              }
            >
              {roles.map((role) => (
                <MenuItem key={role._id} value={role._id}>
                  {`${role.name}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ pb: 2, pr: 3 }}>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={
            !fields.type ||
            (fields.type !== 'm365_email' && Boolean(hostError))
          }
        >
          {fields.type === 'm365_email'
            ? 'Mit Microsoft verbinden'
            : 'Interface testen und Speichern'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
